import { z } from "zod";
import { Id, Minor } from "./common";
import { defineRoutes } from "./define";
import { PROBLEMS } from "./problem";

/** What the Mini App's home screen renders — operational data only, never clinical (product decision). */
export const SelfAppointment = z.object({
  id: Id,
  startsAt: z.string(),
  durationMin: z.number().int(),
  status: z.string(),
});
export const SelfView = z.object({
  patient: z.object({ id: Id, name: z.string(), firstName: z.string(), status: z.string() }),
  nextAppointment: SelfAppointment.nullable(),
  appointments: z.array(SelfAppointment),
  packages: z.array(
    z.object({
      id: Id,
      title: z.string(),
      visitsUsed: z.number().int(),
      visitsTotal: z.number().int(),
      status: z.string(),
    }),
  ),
  invoices: z.array(
    z.object({
      id: Id,
      number: z.string(),
      totalMinor: Minor,
      status: z.string(),
      issuedAt: z.string(),
    }),
  ),
});
export type SelfView = z.infer<typeof SelfView>;

export const PatientSession = z.object({
  /** Opaque bearer token for the Mini App (`Authorization: Bearer pt_…`), short-lived. */
  token: z.string(),
  expiresAt: z.string(),
  patient: z.object({ id: Id, name: z.string(), firstName: z.string() }),
});
export type PatientSession = z.infer<typeof PatientSession>;

export const telegram = defineRoutes({
  /** Exchange Telegram `initData` (HMAC-validated server-side) for a patient session. */
  session: {
    method: "POST",
    path: "/tg/session",
    summary:
      "Mini App sign-in: validated initData → short-lived patient token; 404 when the Telegram account is not linked",
    body: z.object({ initData: z.string().min(1).max(8192) }),
    responses: { 200: PatientSession, 400: PROBLEMS[400], 401: PROBLEMS[401], 404: PROBLEMS[404] },
  },
  me: {
    method: "GET",
    path: "/me",
    summary: "The signed-in patient's own view (patient principal only)",
    responses: { 200: SelfView, 401: PROBLEMS[401], 403: PROBLEMS[403], 404: PROBLEMS[404] },
  },
});

/** Staff side: mint the deep link from the patient card. */
export const TelegramLink = z.object({
  code: z.string(),
  expiresAt: z.string(),
  /** `https://t.me/<bot>?start=<code>` — null when the bot is not configured. */
  deepLink: z.string().nullable(),
});
export type TelegramLink = z.infer<typeof TelegramLink>;
